import { useEffect, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';
import { fetchMetrics } from '../../api/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28BFA', '#FF6B6B', '#3D348B', '#FFB6C1', '#2E8B57', '#DC143C'];

interface SlowestQuery {
    original_dax_query: string;
    execution_time: number;
}

interface Dataset {
    _id: string;
    count: number;
}

interface Metrics {
    average_execution_time: number;
    user_count: number;
    daily_avg_user_msgs: number;
    weekly_avg_user_msgs: number;
    average_messages_per_user: number;
    top_10_slowest_queries: SlowestQuery[];
    top_datasets: Dataset[];
}

const Stats = () => {
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const getMetrics = async () => {
            try {
                const data = await fetchMetrics();
                setMetrics(data);
            } catch (err: any) {
                setError(err.message || 'An error occurred');
            } finally {
                setLoading(false);
            }
        };

        getMetrics();
    }, []);

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    const components = {
        code({ node, inline, className, ...props }: { node: any; inline?: boolean; className?: string }) {
            const language = className?.replace(/language-/, '') || 'text';
            return (
                <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
                    {String(node.children[0].value)}
                </SyntaxHighlighter>
            );
        },
    };

    return (
        <Container>
            <h1>Statistics Dashboard</h1>
            {metrics ? (
                <>
                    <Row>
                        <Col md={6}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Overall Metrics</Card.Title>
                                    <br />
                                    <Card.Text><strong>Average Execution Time:</strong> {metrics.average_execution_time.toFixed(2)} seconds</Card.Text>
                                    <Card.Text><strong>Number of Users:</strong> {metrics.user_count}</Card.Text>
                                    <Card.Text><strong>Daily Average Messages:</strong> {metrics.daily_avg_user_msgs}</Card.Text>
                                    <Card.Text><strong>Weekly Average Messages:</strong> {metrics.weekly_avg_user_msgs}</Card.Text>
                                    <Card.Text><strong>Average Messages per User:</strong> {metrics.average_messages_per_user}</Card.Text>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="mb-3">
                                <Card.Body>
                                    <Card.Title>Top Datasets</Card.Title>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={metrics.top_datasets}
                                                dataKey="count"
                                                nameKey="_id"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                fill="#8884d8"
                                                label
                                            >
                                                {metrics.top_datasets.map((_, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Card.Title>Top 10 Slowest Queries</Card.Title>
                                    <Row xs={1} md={2} lg={2} className="g-3">
                                        {metrics.top_10_slowest_queries.map((query, index) => (
                                            <Col key={index}>
                                                <Card>
                                                    <Card.Body>
                                                        <strong>Execution Time:</strong> {query.execution_time.toFixed(2)}s
                                                        <ReactMarkdown
                                                            children={`\`\`\`dax\n${query.original_dax_query.trim()}\n\`\`\``}
                                                            rehypePlugins={[rehypeRaw]}
                                                            remarkPlugins={[remarkGfm, supersub]}
                                                            components={components}
                                                        />
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            ) : (
                <div>No data available</div>
            )}
        </Container>
    );
};

export default Stats;
